import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  useNavigate,
  useParams
} from 'react-router-dom';
import { BrowserQRCodeReader } from '@zxing/browser';

import { API_BASE } from './config';
import { createMonitorSocket } from '../socket/socket';
import BottomNavigation from './BottomNavigation';
import { resolveAvatarSource } from '../utils/avatar';
import { apiFetch } from '../utils/apiFetch';
import registerAssistantCat from '../assets/register-assistant-cat.webp';

import '../css/Monitor.css';

const MONITOR_GUIDE_STEPS = [
  {
    icon: '🏟️',
    title: 'เกมที่กำลังเล่น = ลงสนาม',
    text: 'ดูคำว่า “ลงสนาม” ใต้เลขสนาม แล้วตรวจชื่อผู้เล่นของคุณ',
    targetId: 'monitor-playing',
    action: 'ไปดูสนาม'
  },
  {
    icon: '🔎',
    title: 'ดูคิวรอลงสนาม',
    text: 'กดแว่นขยาย “คิวลงสนาม” ด้านบน ระบบจะเลื่อนไปยังคิวด้านล่าง',
    targetId: 'monitor-queue',
    action: 'ไปดูคิว'
  },
  {
    icon: '📝',
    title: 'อัปเดตผลการแข่งขัน',
    text: 'เลื่อนไปส่วนผลการแข่งขัน แล้วเลือก T1 Win, Draw หรือ T2 Win',
    targetId: 'monitor-ended',
    action: 'ไปอัปเดตผล'
  }
];


/* =================================================
   HELPERS
================================================= */

const toArray = value => (
  Array.isArray(value)
    ? value
    : Array.isArray(value?.data)
      ? value.data
      : []
);

const urlBase64ToUint8Array = value => {
  const padding = '='.repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(character => character.charCodeAt(0)));
};


const fetchJson = async path => {

  const response = await apiFetch(
    `${API_BASE}${path}`
  );

  const data = await response
    .json()
    .catch(() => ({}));

  if (!response.ok) {

    throw new Error(
      data.message ||
      data.error ||
      `HTTP ${response.status}`
    );

  }

  return data;
};


/* =================================================
   MATCH TIME
================================================= */

const matchTimestamp = match => {

  const timestamp =
    new Date(
      match?.created_at || 0
    ).getTime();

  return Number.isFinite(timestamp)
    ? timestamp
    : 0;
};


const endedMatchTimestamp = match => {

  const timestamp =
    new Date(
      match?.ended_at ||
      match?.end_time ||
      match?.updated_at ||
      match?.created_at ||
      0
    ).getTime();

  return Number.isFinite(timestamp)
    ? timestamp
    : 0;
};

const formatDuration = milliseconds => {
  const totalSeconds = Math.max(0, Math.floor(Number(milliseconds || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .filter((_, index) => hours > 0 || index > 0)
    .map(value => String(value).padStart(2, '0'))
    .join(':');
};

const matchResultLabel = result => ({
  team1_win: '🏆 ทีม 1 ชนะ',
  team2_win: '🏆 ทีม 2 ชนะ',
  draw: '🤝 เสมอ'
}[String(result || '').toLowerCase()] || 'ยังไม่ระบุผล');

/* =================================================
   PLAYER COLOR
================================================= */

const playerColorStyle = player => {

  const source =
    String(
      player?.user_id ??
      player?.name ??
      'player'
    );

  const hash =
    [...source].reduce(
      (total, character) =>
        (
          (
            total * 31
          ) +
          character.charCodeAt(0)
        ) >>> 0,
      0
    );

  const hue =
    Math.round(
      (hash * 137.508) % 360
    );

  return {
    '--monitor-player-ink':
      `hsl(${hue} 62% 31%)`,

    '--monitor-player-bg':
      `hsl(${hue} 86% 95%)`,

    '--monitor-player-border':
      `hsl(${hue} 62% 82%)`
  };
};


/* =================================================
   AVATAR URL
================================================= */

const getAvatarSource = avatarUrl => {
  return resolveAvatarSource(avatarUrl);
};


/* =================================================
   PLAYER BUTTON
================================================= */

function PlayerNameButton({
  player,
  onClick
}) {

  const playerName =
    player?.name ||
    'ไม่ระบุชื่อ';


  /*
   * รองรับหลายชื่อ field
   * เผื่อ Backend ส่งต่างกัน
   */
  const avatarUrl =
    player?.avatarUrl ||
    player?.avatar_url ||
    player?.avatar ||
    '';


  const firstLetter =
    playerName
      .trim()
      .charAt(0)
      .toUpperCase() ||
    '🏸';


  return (

    <button
      type="button"

      className="monitor-player-link"

      style={
        playerColorStyle(player)
      }

      onClick={() =>
        onClick(player)
      }

      title={
        `ดูประวัติการเล่นของ ${playerName}`
      }
    >

      {/* ================= AVATAR ================= */}

      <span className="monitor-player-avatar">

        {avatarUrl ? (

          <img
            src={
              getAvatarSource(
                avatarUrl
              )
            }

            alt={
              `รูปของ ${playerName}`
            }

            onError={event => {

              event.currentTarget.style.display =
                'none';

              const fallback =
                event.currentTarget
                  .nextElementSibling;

              if (fallback) {

                fallback.style.display =
                  'grid';

              }

            }}
          />

        ) : null}


        <span
          className="monitor-player-avatar-fallback"

          style={{
            display:
              avatarUrl
                ? 'none'
                : 'grid'
          }}
        >
          {firstLetter}
        </span>

      </span>


      {/* ================= NAME ================= */}

      <span className="monitor-player-copy">
        <span className="monitor-player-name">
          {playerName}
        </span>

        {player?.playingOtherMatchNo && (
          <small className="monitor-player-busy">
            รอผู้เล่น • เกม #{player.playingOtherMatchNo}
            {player.playingOtherCourtNo ? ` • สนาม ${player.playingOtherCourtNo}` : ''}
          </small>
        )}
      </span>

    </button>

  );

}


/* =================================================
   MATCH CARD
================================================= */

function MatchCard({
  match,
  tone,
  onPlayerClick,
  now,
  onEditResult,
  isSubmitting,
  isAdmin
}) {

  const players =
    Array.isArray(match?.players)
      ? match.players
      : [];


  const team1 =
    players.filter(
      player =>
        Number(player.team) === 1
    );


  const team2 =
    players.filter(
      player =>
        Number(player.team) === 2
    );


  const courtLabel =
    match.court_no
      ? `สนาม ${match.court_no}`
      : '-';

  const startTimestamp = new Date(match.start_time || match.created_at || 0).getTime();
  const endTimestamp = endedMatchTimestamp(match);
  const duration = startTimestamp > 0
    ? formatDuration((tone === 'ended' ? endTimestamp : now) - startTimestamp)
    : '--:--';
  const normalizedResult = String(match.result || '').toLowerCase();
  const editWindowOpen =
    isAdmin ||
    (endTimestamp > 0 && now - endTimestamp <= 5 * 60 * 1000);
  const team1Won = tone === 'ended' && normalizedResult === 'team1_win';
  const team2Won = tone === 'ended' && normalizedResult === 'team2_win';
  const isDraw = tone === 'ended' && normalizedResult === 'draw';
  const isRecentlyStarted = tone === 'playing' && startTimestamp > 0 && now - startTimestamp >= 0 && now - startTimestamp <= 2 * 60 * 1000;
  const courtColorIndex = ((Math.max(1, Number(match.court_no) || 1) - 1) % 6) + 1;


  return (

    <article
      className={
        `monitor-match-card ${tone}${tone === 'playing' ? ` court-color-${courtColorIndex}` : ''}${isRecentlyStarted ? ' recently-started' : ''}${team1Won ? ' team1-winner' : ''}${team2Won ? ' team2-winner' : ''}${isDraw ? ' match-draw' : ''}`
      }
    >

      {/* ================= HEADER ================= */}

      <header>

        <span>

          เกม #

          {
            match.groupMatchNo ||
            match.matchId
          }

          {isRecentlyStarted && <em className="monitor-new-match-badge">✨ เพิ่งลงสนาม</em>}

        </span>


        <strong className={tone === 'playing' ? 'monitor-court-badge' : ''}>

          {
            tone === 'playing'
              ? courtLabel

              : tone === 'ended'
                ? `✅ จบแล้ว • ${courtLabel}`

                : 'รอสนาม'
          }

          {tone === 'playing' && <small>ลงสนาม</small>}

        </strong>

      </header>


      {/* ================= TEAM ================= */}

      <div className="monitor-match-teams">


        {/* ================= TEAM 1 ================= */}

        <div className={team1Won ? 'winner-team' : team2Won ? 'loser-team' : ''}>

          <small>
            T1
          </small>


          {team1.map(player => (

            <PlayerNameButton
              key={
                player.user_id
              }

              player={
                player
              }

              onClick={
                onPlayerClick
              }
            />

          ))}

        </div>


        {/* ================= VS ================= */}

        <b>
          VS
        </b>


        {/* ================= TEAM 2 ================= */}

        <div className={team2Won ? 'winner-team' : team1Won ? 'loser-team' : ''}>

          <small>
            T2
          </small>


          {team2.map(player => (

            <PlayerNameButton
              key={
                player.user_id
              }

              player={
                player
              }

              onClick={
                onPlayerClick
              }
            />

          ))}

        </div>

      </div>

      {tone === 'playing' && (
        <div className="monitor-playing-duration" aria-label="เวลาแข่งขัน">
          <span className="monitor-match-duration">⏱ {duration}</span>
        </div>
      )}

      {tone === 'ended' && (
        <div className="monitor-ended-summary">
          <span><b>{matchResultLabel(match.result)}</b> • เวลาเล่น {duration}</span>
          <div className="monitor-result-actions" aria-label="อัปเดตผลการแข่งขัน">
            <button className={normalizedResult === 'team1_win' ? 'selected' : ''} disabled={isSubmitting || !editWindowOpen} onClick={() => onEditResult(match, 'TEAM1')}>T1 Win</button>
            <button className={normalizedResult === 'draw' ? 'selected' : ''} disabled={isSubmitting || !editWindowOpen} onClick={() => onEditResult(match, 'DRAW')}>Draw</button>
            <button className={normalizedResult === 'team2_win' ? 'selected' : ''} disabled={isSubmitting || !editWindowOpen} onClick={() => onEditResult(match, 'TEAM2')}>T2 Win</button>
          </div>
          {!editWindowOpen && (
            <small className="monitor-result-locked">🔒 เกิน 5 นาที เฉพาะผู้จัดเท่านั้นที่แก้ผลได้</small>
          )}
          {isAdmin && endTimestamp > 0 && now - endTimestamp > 5 * 60 * 1000 && (
            <small className="monitor-result-admin">🛡 ผู้จัดแก้ผลย้อนหลังได้</small>
          )}
        </div>
      )}

    </article>

  );

}


/* =================================================
   MATCH BOARD
================================================= */

function MatchBoard({
  matches,
  courts,
  onPlayerClick,
  onEditResult,
  submittingMatchId,
  isAdmin
}) {

  // Keep the live clock inside the match board so the full Monitor page does
  // not rerender every second (groups, alerts, navigation and modals stay put).
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);


  /* ================= PLAYING ================= */

  const playingMatches =
    useMemo(
      () =>
        matches
          .filter(
            match =>
              String(
                match.status
              ).toLowerCase() ===
              'playing'
          )
          .sort(
            (left, right) =>
              Number(
                left.court_no ||
                999
              ) -
              Number(
                right.court_no ||
                999
              )
          ),

      [matches]
    );


  /* ================= QUEUE ================= */

  const queueMatches =
    useMemo(
      () =>
        matches
          .filter(
            match =>
              [
                'waiting',
                'waiting_queue'
              ].includes(
                String(
                  match.status
                ).toLowerCase()
              )
          )
          .sort(
            (left, right) =>

              Number(
                left.queue_no ||
                999999
              ) -

                Number(
                  right.queue_no ||
                  999999
                )

              ||

              matchTimestamp(
                left
              ) -

                matchTimestamp(
                  right
                )

              ||

              Number(
                left.matchId
              ) -

                Number(
                  right.matchId
                )
          ),

      [matches]
    );

  const playingCourtSlots = useMemo(() => {
    if (!courts?.length) return playingMatches.map(match => ({ courtNo: Number(match.court_no || 0), match }));
    return [...courts]
      .sort((left, right) => Number(left.court_no || 0) - Number(right.court_no || 0))
      .map(court => ({
        courtNo: Number(court.court_no),
        court,
        match: playingMatches.find(item => Number(item.court_no || item.courtNo) === Number(court.court_no)) || null
      }));
  }, [courts, playingMatches]);


  /* ================= ENDED ================= */

  const endedMatches =
    useMemo(
      () =>
        matches
          .filter(
            match =>
              [
                'ended',
                'end',
                'completed',
                'finished'
              ].includes(
                String(
                  match.status || ''
                )
                  .trim()
                  .toLowerCase()
              )
          )
          .sort(
            (left, right) =>

              endedMatchTimestamp(
                right
              ) -

                endedMatchTimestamp(
                  left
                )

              ||

              Number(
                right.matchId ||
                0
              ) -

                Number(
                  left.matchId ||
                  0
                )
          ),

      [matches]
    );


  /* =================================================
     RENDER
  ================================================= */

  return (

    <>

      {/* =================================================
         QUEUE + PLAY
      ================================================= */}

      <section
        className="monitor-match-board"
        aria-label="เกมที่กำลังเล่นและคิวรอลงสนาม"
      >


        {/* ================= QUEUE ================= */}

        <div className="monitor-match-column" id="monitor-queue">

          <header
            className="
              monitor-match-heading
              queue-heading
            "
          >

            <span>

              <strong>
                คิวรอลงสนาม
              </strong>

              <small>
                Match ที่กำลังรอลงสนามตามลำดับคิว
              </small>

            </span>


            <b>
              {queueMatches.length} Match
            </b>

          </header>


          <div className="monitor-match-list">

            {
              queueMatches.length > 0

                ? queueMatches.map(
                    match => (

                      <MatchCard
                        key={
                          match.matchId
                        }

                        match={
                          match
                        }

                        tone="waiting"

                        onPlayerClick={
                          onPlayerClick
                        }
                        now={now}
                      />

                    )
                  )

                : (

                  <div className="monitor-empty">

                    ยังไม่มี Match ในคิว

                  </div>

                )
            }

          </div>

        </div>


        {/* ================= PLAY ================= */}

        <div className="monitor-match-column" id="monitor-playing">

          <header
            className="
              monitor-match-heading
              play-heading
            "
          >

            <span>

              <strong>
                เกมที่กำลังเล่น
              </strong>

              <small>
                Match ที่กำลังแข่งขันอยู่
              </small>

            </span>


            <b>
              {playingMatches.length} Match
            </b>

          </header>


          <div className="monitor-match-list">

            {
              playingCourtSlots.length > 0

                ? playingCourtSlots.map(
                    slot => slot.match ? (

                      <MatchCard
                        key={
                          slot.match.matchId
                        }

                        match={
                          slot.match
                        }

                        tone="playing"

                        onPlayerClick={
                          onPlayerClick
                        }
                        now={now}
                      />

                    ) : <article className={`monitor-court-empty-card court-color-${((Math.max(1, Number(slot.courtNo) || 1) - 1) % 6) + 1}`} key={`court-${slot.courtNo}`}><header><span>สนาม {slot.courtNo}</span><b>ว่าง</b></header><div><i>🏸</i><strong>พร้อมรับ Match ถัดไป</strong><small>ยังไม่มีเกมกำลังแข่งขัน</small></div></article>
                  )

                : (

                  <div className="monitor-empty">

                    ยังไม่มี Match ที่กำลังเล่น

                  </div>

                )
            }

          </div>

        </div>

      </section>


      {/* =================================================
         ENDED
      ================================================= */}

      <section className="monitor-ended-section" id="monitor-ended">

        <header
          className="
            monitor-match-heading
            ended-heading
          "
        >

          <span>

            <strong>
              📝 อัปเดตผลการแข่งขัน
            </strong>

            <small>
              เลือกผลของ Match ที่เล่นจบแล้ว
            </small>

          </span>


          <b>
            {endedMatches.length} Match
          </b>

        </header>


        <div className="monitor-ended-list">

          {
            endedMatches.length > 0

              ? endedMatches.map(
                  match => (

                    <MatchCard
                      key={
                        match.matchId
                      }

                      match={
                        match
                      }

                      tone="ended"

                      onPlayerClick={
                        onPlayerClick
                      }
                      now={now}
                      onEditResult={onEditResult}
                      isSubmitting={Number(submittingMatchId) === Number(match.matchId)}
                      isAdmin={isAdmin}
                    />

                  )
                )

              : (

                <div className="monitor-empty">

                  ยังไม่มี Match ที่จบแล้ว

                </div>

              )
          }

        </div>

      </section>

    </>

  );

}


/* =================================================
   MONITOR
================================================= */

function Monitor() {

  /* ================= ROUTER ================= */

  const {
    groupId: routeGroupId
  } = useParams();


  const navigate =
    useNavigate();


  const [selectedGroupId, setSelectedGroupId] = useState(() =>
    Number(routeGroupId) || Number(localStorage.getItem('last_group_id')) || null
  );

  const loggedInUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    if (selectedGroupId) localStorage.setItem('last_group_id', String(selectedGroupId));
  }, [selectedGroupId]);

  // Keep old shared links working, but expose the clean canonical URL.
  useEffect(() => {
    if (routeGroupId) navigate('/monitor', { replace: true });
  }, [navigate, routeGroupId]);


  /* ================= STATE ================= */

  const [groups, setGroups] =
    useState([]);

  const [matches, setMatches] =
    useState([]);

  const [courts, setCourts] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState('');

  const [submittingMatchId, setSubmittingMatchId] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutMessage, setCheckoutMessage] = useState('');
  const [, setMyGroupStatus] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const [onlineViewerCount, setOnlineViewerCount] = useState(0);
  const [inAppAlert, setInAppAlert] = useState(null);
  const [showMonitorHelp, setShowMonitorHelp] = useState(false);
  const [showGuideBot, setShowGuideBot] = useState(true);
  const [guideImageFailed, setGuideImageFailed] = useState(false);
  const [guideStep, setGuideStep] = useState(0);
  const [guideBotPosition, setGuideBotPosition] = useState(null);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [waitAlertBusy, setWaitAlertBusy] = useState(false);
  const [waitAlertOpen, setWaitAlertOpen] = useState(false);
  const [waitAlertFeedback, setWaitAlertFeedback] = useState('');
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [qrScanMessage, setQrScanMessage] = useState('');
  const [qrScanError, setQrScanError] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [waitBellPosition, setWaitBellPosition] = useState(() => {
    try { return JSON.parse(localStorage.getItem('monitor_wait_bell_position') || 'null'); }
    catch { return null; }
  });
  const audioContextRef = useRef(null);
  const notifiedMatchStatesRef = useRef(new Map());
  const waitBellDragRef = useRef(null);
  const waitBellMovedRef = useRef(false);
  const guideBotDragRef = useRef(null);
  const guideBotMovedRef = useRef(false);
  const qrVideoRef = useRef(null);
  const qrControlsRef = useRef(null);


  const requestVersionRef =
    useRef(0);

  useEffect(() => {
    if (waitBellPosition) localStorage.setItem('monitor_wait_bell_position', JSON.stringify(waitBellPosition));
  }, [waitBellPosition]);

  useEffect(() => {
    const keepWaitBellAboveNavigation = () => {
      setWaitBellPosition(position => {
        if (!position) return position;
        const size = window.innerWidth <= 700 ? 64 : 72;
        const bottomNavTop = document.querySelector('.app-bottom-nav')?.getBoundingClientRect().top
          || window.innerHeight;
        const next = {
          x: Math.max(8, Math.min(window.innerWidth - size - 8, Number(position.x) || 8)),
          y: Math.max(8, Math.min(bottomNavTop - size - 10, Number(position.y) || 8))
        };
        return next.x === position.x && next.y === position.y ? position : next;
      });
    };
    const frame = window.requestAnimationFrame(keepWaitBellAboveNavigation);
    window.addEventListener('resize', keepWaitBellAboveNavigation);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', keepWaitBellAboveNavigation);
    };
  }, []);

  useEffect(() => {
    const updateScrollTopVisibility = () => setShowScrollTop(window.scrollY > 360);
    updateScrollTopVisibility();
    window.addEventListener('scroll', updateScrollTopVisibility, { passive: true });
    return () => window.removeEventListener('scroll', updateScrollTopVisibility);
  }, []);

  useEffect(() => {
    if (!showMonitorHelp) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape') setShowMonitorHelp(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('monitor-help-open');

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('monitor-help-open');
    };
  }, [showMonitorHelp]);

  const startGuideBotDrag = (event, allowButton = false) => {
    if (event.button !== 0 || (!allowButton && event.target.closest('button'))) return;
    const panel = event.currentTarget.closest('.monitor-guide-bot');
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    guideBotMovedRef.current = false;
    guideBotDragRef.current = {
      pointerId: event.pointerId,
      pointerX: event.clientX,
      pointerY: event.clientY,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveGuideBot = event => {
    const drag = guideBotDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (Math.abs(event.clientX - drag.pointerX) + Math.abs(event.clientY - drag.pointerY) > 5) {
      guideBotMovedRef.current = true;
    }
    const bottomNavTop = document.querySelector('.app-bottom-nav')?.getBoundingClientRect().top
      || window.innerHeight;
    setGuideBotPosition({
      left: Math.max(8, Math.min(window.innerWidth - drag.width - 8, drag.left + event.clientX - drag.pointerX)),
      top: Math.max(8, Math.min(bottomNavTop - drag.height - 8, drag.top + event.clientY - drag.pointerY))
    });
  };

  const stopGuideBotDrag = event => {
    if (guideBotDragRef.current?.pointerId !== event.pointerId) return;
    guideBotDragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const collapseGuideBot = () => {
    const bell = document.querySelector('.monitor-wait-bell-wrap')?.getBoundingClientRect();
    const bottomNavTop = document.querySelector('.app-bottom-nav')?.getBoundingClientRect().top
      || window.innerHeight;
    const botSize = window.innerWidth <= 600 ? 62 : 72;
    if (bell) {
      setGuideBotPosition({
        left: Math.max(8, Math.min(window.innerWidth - botSize - 8, bell.left + ((bell.width - botSize) / 2))),
        top: Math.max(8, Math.min(bottomNavTop - botSize - 8, bell.top - botSize - 10))
      });
    } else {
      setGuideBotPosition({
        left: Math.max(8, window.innerWidth - botSize - 12),
        top: Math.max(8, bottomNavTop - botSize - 90)
      });
    }
    setShowGuideBot(false);
  };

  const expandGuideBot = () => {
    guideBotMovedRef.current = false;
    setGuideBotPosition(null);
    setShowGuideBot(true);
  };

  const openGuideTarget = targetId => {
    document.getElementById(targetId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const sendWaitAlert = async message => {
    if (!loggedInUser?.user_id || !localStorage.getItem('token')) return;
    if (!selectedGroupId) { setWaitAlertFeedback('กรุณาเลือกก๊วนก่อนแจ้งผู้จัด'); return; }
    setWaitAlertBusy(true); setWaitAlertFeedback('');
    try {
      const response = await fetch(`${API_BASE}/api/match-planner/${Number(selectedGroupId)}/wait-alert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify({ message })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'แจ้งผู้จัดไม่สำเร็จ');
      setWaitAlertFeedback(result.alreadySent ? 'ส่งข้อความนี้ไว้แล้ว กรุณารอสักครู่' : 'ส่งข้อความถึงผู้จัดแล้ว');
      setWaitAlertOpen(false);
    } catch (waitError) { setWaitAlertFeedback(waitError.message || 'แจ้งผู้จัดไม่สำเร็จ'); }
    finally { setWaitAlertBusy(false); }
  };

  const moveWaitBell = event => {
    const drag = waitBellDragRef.current;
    if (!drag) return;
    const deltaX = event.clientX - drag.pointerX;
    const deltaY = event.clientY - drag.pointerY;
    if (Math.abs(deltaX) + Math.abs(deltaY) > 5) waitBellMovedRef.current = true;
    const bottomNavTop = document.querySelector('.app-bottom-nav')?.getBoundingClientRect().top
      || window.innerHeight;
    setWaitBellPosition({
      x: Math.max(8, Math.min(window.innerWidth - drag.width - 8, drag.left + deltaX)),
      y: Math.max(8, Math.min(bottomNavTop - drag.height - 10, drag.top + deltaY))
    });
  };

  const finishWaitBellDrag = event => {
    if (!waitBellDragRef.current) return;
    waitBellDragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const handleMonitorQrValue = useCallback(async rawValue => {
    try {
      setQrScanError(false);
      setQrScanMessage('กำลังตรวจสอบ QR...');
      const qrUrl = new URL(rawValue, window.location.origin);
      const code = qrUrl.searchParams.get('code');
      if (!/^\/join\/\d+$/i.test(qrUrl.pathname) || !code) {
        throw new Error('QR นี้ไม่ใช่ QR Check-in ของระบบ');
      }
      const qrGroupId = Number(qrUrl.pathname.split('/').filter(Boolean)[1]);
      const infoResponse = await apiFetch(`${API_BASE}/api/checkin/join/${qrGroupId}/info?code=${encodeURIComponent(code)}`);
      const groupData = await infoResponse.json().catch(() => ({}));
      if (!infoResponse.ok) throw new Error(groupData.message || 'QR Code ไม่ถูกต้อง');

      const statusResponse = await apiFetch(`${API_BASE}/api/checkin/status/${Number(loggedInUser.user_id)}`);
      const statusData = await statusResponse.json().catch(() => ({}));
      const isCurrentGroup = Number(statusData?.group_id) === qrGroupId;
      if (isCurrentGroup && String(statusData?.status || '').toUpperCase() === 'HOLD') {
        const response = await apiFetch(`${API_BASE}/api/checkin/join/${qrGroupId}/checkout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'เช็คอินไม่สำเร็จ');
        setQrScanMessage(data.message || `เช็คอิน ${groupData.Group_Name} สำเร็จ`);
      } else if (isCurrentGroup) {
        setQrScanMessage(`คุณเช็คอิน ${groupData.Group_Name} อยู่แล้ว`);
      } else {
        const response = await apiFetch(`${API_BASE}/api/checkin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: Number(loggedInUser.user_id), Group_Name: groupData.Group_Name })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'เช็คอินด้วย QR ไม่สำเร็จ');
        localStorage.setItem('last_group_id', String(qrGroupId));
        setSelectedGroupId(qrGroupId);
        setQrScanMessage(`สแกน QR และเช็คอิน ${groupData.Group_Name} สำเร็จ`);
      }
    } catch (scanError) {
      setQrScanError(true);
      setQrScanMessage(scanError.message || 'อ่าน QR ไม่สำเร็จ');
    }
  }, [loggedInUser?.user_id]);

  const openMonitorQrScanner = () => {
    if (!loggedInUser?.user_id || !localStorage.getItem('token')) {
      navigate('/login');
      return;
    }
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setQrScanError(true);
      setQrScanMessage('ไม่สามารถเปิดกล้องได้ กรุณาเปิดเว็บไซต์ผ่าน HTTPS และอนุญาตสิทธิ์กล้อง');
      return;
    }
    setQrScanError(false);
    setQrScanMessage('กำลังเปิดตัวสแกน QR...');
    setQrScannerOpen(true);
  };

  useEffect(() => {
    if (!qrScannerOpen || !qrVideoRef.current) return undefined;
    let cancelled = false;
    const reader = new BrowserQRCodeReader();
    const startScanner = async () => {
      try {
        const devices = await BrowserQRCodeReader.listVideoInputDevices();
        if (cancelled) return;
        const rearCamera = devices.find(device => /back|rear|environment|หลัง/i.test(String(device.label || '')))
          || devices[devices.length - 1];
        const controls = await reader.decodeFromVideoDevice(rearCamera?.deviceId, qrVideoRef.current, (result, error, scannerControls) => {
          void error;
          if (!result || cancelled) return;
          cancelled = true;
          scannerControls.stop();
          setQrScannerOpen(false);
          handleMonitorQrValue(result.getText());
        });
        if (cancelled) { controls.stop(); return; }
        qrControlsRef.current = controls;
        setQrScanMessage('หันกล้องหลังไปที่ QR Code ระบบจะอ่านให้อัตโนมัติ');
      } catch (cameraError) {
        if (cancelled) return;
        setQrScannerOpen(false);
        setQrScanError(true);
        setQrScanMessage(/permission|notallowed|denied/i.test(String(cameraError?.name || cameraError?.message || ''))
          ? 'กรุณาอนุญาตสิทธิ์กล้องให้เว็บไซต์แล้วลองใหม่'
          : 'เปิดตัวสแกน QR ไม่สำเร็จ กรุณาตรวจว่ากล้องไม่ได้ถูกแอปอื่นใช้งาน');
      }
    };
    startScanner();
    return () => {
      cancelled = true;
      qrControlsRef.current?.stop();
      qrControlsRef.current = null;
    };
  }, [handleMonitorQrValue, qrScannerOpen]);

  const unlockAlertAudio = useCallback(() => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!audioContextRef.current) audioContextRef.current = new AudioContextClass();
    if (audioContextRef.current.state === 'suspended') audioContextRef.current.resume().catch(() => {});
    return audioContextRef.current;
  }, []);

  const ringAlertBell = useCallback(() => {
    const context = unlockAlertAudio();
    if (!context || context.state !== 'running') return;
    const playTone = (frequency, delay, duration, volume) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, context.currentTime + delay);
      gain.gain.setValueAtTime(0.0001, context.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(volume, context.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + delay + duration);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(context.currentTime + delay);
      oscillator.stop(context.currentTime + delay + duration + 0.03);
    };
    playTone(880, 0, 0.34, 0.24);
    playTone(1175, 0.2, 0.48, 0.2);
    playTone(880, 0.72, 0.34, 0.2);
    playTone(1175, 0.92, 0.55, 0.18);
  }, [unlockAlertAudio]);

  const showMatchAlert = useCallback((match, status) => {
    const matchNo = match.group_match_no || match.groupMatchNo || match.id;
    const matchId = Number(match.id || match.matchId);
    const acknowledgementKey = `monitor_match_alert_ack_${Number(selectedGroupId)}_${Number(loggedInUser?.user_id)}_${matchId}_${status}`;
    if (localStorage.getItem(acknowledgementKey) === '1') return;
    const isPlaying = status === 'PLAYING';
    setInAppAlert({
      acknowledgementKey,
      type: isPlaying ? 'PLAY' : 'QUEUE',
      title: isPlaying ? '📣 ถึงเวลาลงสนาม' : '🏸 ใกล้ถึงคิวของคุณแล้ว',
      body: isPlaying
        ? `เกม #${matchNo} • สนาม ${match.court_no || match.courtNo || '-'}`
        : `เกม #${matchNo} อยู่ในคิวแล้ว กรุณาเตรียมตัว`
    });
    ringAlertBell();
    if ('vibrate' in navigator) navigator.vibrate(isPlaying ? [350, 120, 350, 120, 650] : [250, 100, 250]);
  }, [loggedInUser?.user_id, ringAlertBell, selectedGroupId]);

  useEffect(() => {
    const unlockOnce = () => unlockAlertAudio();
    window.addEventListener('pointerdown', unlockOnce, { once: true });
    window.addEventListener('keydown', unlockOnce, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlockOnce);
      window.removeEventListener('keydown', unlockOnce);
    };
  }, [unlockAlertAudio]);

  useEffect(() => {
    if (loading || !loggedInUser?.user_id) return;
    const userId = Number(loggedInUser.user_id);
    const current = new Map();
    const relevant = matches.filter(match =>
      (Array.isArray(match.players) ? match.players : []).some(player => Number(player.user_id) === userId)
    );

    relevant.forEach(match => {
      const status = String(match.status || '').trim().toUpperCase();
      const normalized = status === 'PLAYING'
        ? 'PLAYING'
        : ['WAITING', 'WAITING_QUEUE', 'QUEUE'].includes(status) ? 'QUEUE' : '';
      if (!normalized) return;
      const matchId = Number(match.id || match.matchId);
      current.set(matchId, normalized);
      const previous = notifiedMatchStatesRef.current.get(matchId);
      if (document.visibilityState === 'visible' && previous !== normalized) showMatchAlert(match, normalized);
    });

    notifiedMatchStatesRef.current = current;
  }, [loading, loggedInUser?.user_id, matches, showMatchAlert]);

  useEffect(() => {
    if (!inAppAlert) return undefined;
    const timer = window.setTimeout(() => setInAppAlert(null), 15000);
    return () => window.clearTimeout(timer);
  }, [inAppAlert]);

  useEffect(() => {
    if (!loggedInUser?.user_id) return;
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isIos && !isStandalone) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    let cancelled = false;
    navigator.serviceWorker.register('/push-sw.js')
      .then(registration => registration.pushManager.getSubscription())
      .then(subscription => {
        if (cancelled) return;
        const enabled = Boolean(subscription);
        setPushEnabled(enabled);
        if (
          !enabled &&
          Notification.permission !== 'denied' &&
          sessionStorage.getItem('monitor_push_prompt_dismissed') !== '1'
        ) setShowPushPrompt(true);
      })
      .catch(() => { if (!cancelled) setPushEnabled(false); });
    return () => { cancelled = true; };
  }, [loggedInUser?.user_id]);

  useEffect(() => {
    if (!loggedInUser?.user_id || !selectedGroupId) return;
    fetch(`${API_BASE}/api/checkin/status/${loggedInUser.user_id}`)
      .then(response => response.json())
      .then(data => {
        if (Number(data.group_id) === Number(selectedGroupId)) setMyGroupStatus(String(data.status || '').toUpperCase());
      })
      .catch(() => {});
  }, [loggedInUser?.user_id, selectedGroupId]);


  const loadCourts = useCallback(async () => {
    if (!selectedGroupId) {
      setCourts([]);
      return;
    }
    try {
      const data = await fetchJson(`/api/courts/${selectedGroupId}/all`);
      setCourts(toArray(data));
    } catch (courtError) {
      console.error('[MONITOR COURTS]', courtError);
    }
  }, [selectedGroupId]);

  /* =================================================
     LOAD GROUP
  ================================================= */

  const loadGroups =
    useCallback(
      async () => {

        const data =
          await fetchJson(
            '/api/groups'
          );

        const nextGroups = toArray(data).filter(group =>
          !['CLOSE', 'CLOSED'].includes(
            String(group.Group_Status || '').trim().toUpperCase()
          )
        );

        setGroups(
          nextGroups
        );

        return nextGroups;

      },
      []
    );


  /* =================================================
     LOAD MATCHES
  ================================================= */

  const loadMatches =
    useCallback(
      async ({
        silent = false
      } = {}) => {

        if (
          !selectedGroupId
        ) {

          setMatches([]);
          setCourts([]);

          setLoading(false);

          return;

        }


        const requestVersion =
          ++requestVersionRef.current;


        if (silent) {

          setRefreshing(true);

        } else {

          setLoading(true);

        }


        try {

          const data = await fetchJson(`/api/game-match/group/${selectedGroupId}`);


          if (
            requestVersion !==
            requestVersionRef.current
          ) {

            return;

          }


          setMatches(
            toArray(data)
          );


          setError('');


        } catch (loadError) {

          if (
            requestVersion !==
            requestVersionRef.current
          ) {

            return;

          }


          console.error(
            '[MONITOR MATCH]',
            loadError
          );


          setError(
            loadError.message ||
            'โหลดข้อมูลคิวและสนามไม่สำเร็จ'
          );


        } finally {

          if (
            requestVersion ===
            requestVersionRef.current
          ) {

            setLoading(false);

            setRefreshing(false);

          }

        }

      },

      [selectedGroupId]
    );


  /* =================================================
     LOAD GROUP
  ================================================= */

  useEffect(() => {

    loadGroups()
      .catch(
        loadError => {

          setError(
            loadError.message ||
            'โหลดรายการ Group ไม่สำเร็จ'
          );

          setLoading(false);

      }
    );

  }, [loadGroups]);

  // Keep the waiting screen current even if a production proxy temporarily
  // misses a Socket.IO group event.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadGroups().catch(() => {});
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [loadGroups]);


  /* =================================================
     VALIDATE GROUP
  ================================================= */

  useEffect(() => {

    if (
      groups.length === 0
    ) {
      setSelectedGroupId(null);
      return;
    }


    const selectedExists =
      groups.some(
        group =>
          Number(group.id) ===
          Number(selectedGroupId)
      );


    if (
      !selectedExists
    ) {

      setSelectedGroupId(Number(groups[0].id));

    }

  }, [
    groups,
    selectedGroupId
  ]);


  /* =================================================
     LOAD MATCH
  ================================================= */

  useEffect(() => {

    loadMatches();
    loadCourts();

  }, [loadMatches, loadCourts]);


  /* =================================================
     FALLBACK 30 SEC
  ================================================= */

  useEffect(() => {

    if (
      !selectedGroupId
    ) {
      return undefined;
    }


    const intervalId =
      window.setInterval(
        () =>
          loadMatches({
            silent: true
          }),

        30000
      );


    return () => {

      window.clearInterval(
        intervalId
      );

    };

  }, [
    loadMatches,
    selectedGroupId
  ]);


  /* =================================================
     SOCKET
  ================================================= */

  useEffect(() => {
    const monitorSocket =
      createMonitorSocket();


    const joinGroup = () => {

      monitorSocket.emit('join-monitor-view');

      if (!selectedGroupId) return;

      monitorSocket.emit(
        'join-group',
        selectedGroupId
      );

    };


    const refreshMatches =
      (payload = {}) => {

        const eventGroupId =
          payload.groupId ??
          payload.group_id;


        if (
          eventGroupId &&
          Number(eventGroupId) !==
            selectedGroupId
        ) {

          return;

        }


        loadMatches({
          silent: true
        });

      };


    const matchEvents = [

      'match-generated',

      'match-started',

      'match-ended',

      'match-result-updated',

      'match-updated',

      'queue-updated',

      'match-cancelled',

      // เพิ่มไว้เผื่อ avatar update
      'player-avatar-updated'

    ];


    monitorSocket.on(
      'connect',
      joinGroup
    );

    const updateViewerCount = payload => {
      setOnlineViewerCount(Math.max(0, Number(payload?.count || 0)));
    };
    monitorSocket.on('monitor-viewers-updated', updateViewerCount);


    matchEvents.forEach(
      eventName =>

        monitorSocket.on(
          eventName,
          refreshMatches
        )
    );

    const courtEvents = ['court-status-updated', 'courts-added', 'courts-initialized', 'court-deleted'];
    courtEvents.forEach(eventName => monitorSocket.on(eventName, loadCourts));

    const refreshGroups = () => loadGroups().catch(() => {});
    const groupEvents = [
      'group-created',
      'group-opened',
      'group-closed',
      'group-deleted'
    ];
    groupEvents.forEach(eventName => {
      monitorSocket.on(eventName, refreshGroups);
    });


    if (
      monitorSocket.connected
    ) {

      joinGroup();

    }


    return () => {

      monitorSocket.off(
        'connect',
        joinGroup
      );

      monitorSocket.off('monitor-viewers-updated', updateViewerCount);


      matchEvents.forEach(
        eventName =>

          monitorSocket.off(
            eventName,
            refreshMatches
          )
      );
      courtEvents.forEach(eventName => monitorSocket.off(eventName, loadCourts));

      groupEvents.forEach(eventName => {
        monitorSocket.off(eventName, refreshGroups);
      });


      monitorSocket.disconnect();

    };

  }, [
    loadGroups,
    loadCourts,
    loadMatches,
    selectedGroupId
  ]);


  /* =================================================
     SELECT GROUP
  ================================================= */

  const selectedGroup =
    groups.find(
      group =>
        Number(group.id) ===
        Number(selectedGroupId)
    );

  const submitMatchResult = useCallback(async (match, winnerTeam, editing = false) => {
    if (!match?.matchId || submittingMatchId !== null) return;

    const action = editing ? 'แก้ไขผล' : 'จบ Match';

    try {
      setSubmittingMatchId(match.matchId);
      const response = await fetch(
        editing
          ? `${API_BASE}/api/game-match/${match.matchId}/result`
          : `${API_BASE}/api/game-match/end`,
        {
          method: editing ? 'PATCH' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('token')
              ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
              : {})
          },
          body: JSON.stringify({ matchId: match.matchId, winnerTeam })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || `${action}ไม่สำเร็จ`);
      await loadMatches({ silent: true });
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmittingMatchId(null);
    }
  }, [loadMatches, submittingMatchId]);


  /* =================================================
     PLAYER HISTORY
  ================================================= */

  const openPlayerHistory =
    player => {

      if (
        !player?.user_id ||
        !selectedGroupId
      ) {

        return;

      }


      navigate(
        `/player-history/1/${player.user_id}`
      );

    };


  /* =================================================
     RANK
  ================================================= */

  const openRankTable =
    () => {

      navigate(
        '/rank/'
      );

    };

  const openMyProfile = () => {
    const userId = Number(loggedInUser?.user_id);
    if (!userId) return;
    navigate('/profile');
  };

  const openNotificationLogin = () => {
    const returnPath = '/monitor';
    navigate(`/?redirect=${encodeURIComponent(returnPath)}`);
  };

  const togglePushNotifications = async () => {
    if (pushBusy) return;
    const token = localStorage.getItem('device_token') || localStorage.getItem('token');
    if (!token) {
      setPushMessage('กรุณาเข้าสู่ระบบก่อนเปิดแจ้งเตือน');
      return;
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setPushMessage('แอปเปิดเว็บไซต์นี้ยังไม่รองรับการแจ้งเตือน');
      return;
    }
    try {
      setPushBusy(true);
      setPushMessage('');
      const registration = await navigator.serviceWorker.register('/push-sw.js');
      await navigator.serviceWorker.ready;
      const current = await registration.pushManager.getSubscription();
      if (pushEnabled || current) {
        await fetch(`${API_BASE}/api/push/unsubscribe`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ endpoint: current?.endpoint })
        });
        if (current) await current.unsubscribe();
        setPushEnabled(false);
        setPushMessage('ปิดแจ้งเตือนแล้ว');
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') throw new Error('กรุณาอนุญาตการแจ้งเตือนในแอปเปิดเว็บไซต์');
      const keyResponse = await fetch(`${API_BASE}/api/push/public-key`);
      const keyData = await keyResponse.json().catch(() => ({}));
      if (!keyResponse.ok) throw new Error(keyData.message || 'โหลด Push Key ไม่สำเร็จ');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyData.publicKey)
      });
      const response = await fetch(`${API_BASE}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscription: subscription.toJSON() })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'เปิดแจ้งเตือนไม่สำเร็จ');
      setPushEnabled(true);
      setShowPushPrompt(false);
      setPushMessage('✓ เปิดแจ้งเตือนแล้ว');
      await fetch(`${API_BASE}/api/push/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url: '/monitor' })
      });
    } catch (pushError) {
      setPushMessage(pushError.message);
    } finally {
      setPushBusy(false);
    }
  };

  const logoutMyself = async () => {
    if (checkingOut) return;
    const token = localStorage.getItem('device_token') || localStorage.getItem('token');
    if (!token) {
      setCheckoutMessage('กรุณาเข้าสู่ระบบก่อนออกจากระบบ');
      return;
    }
    if (!window.confirm('ต้องการออกจากระบบใช่หรือไม่?\nสถานะของคุณในทุกก๊วนจะเปลี่ยนเป็นพักชั่วคราว')) return;
    try {
      setCheckingOut(true);
      setCheckoutMessage('');
      const response = await fetch(`${API_BASE}/api/checkin/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'ออกจากระบบไม่สำเร็จ');
      localStorage.removeItem('token');
      localStorage.removeItem('device_token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('loginRedirect');
      setMyGroupStatus('HOLD');
      setCheckoutMessage('✓ ออกจากระบบแล้ว • ทุกก๊วนเปลี่ยนเป็นพักชั่วคราว');
    } catch (checkoutError) {
      setCheckoutMessage(checkoutError.message);
    } finally {
      setCheckingOut(false);
    }
  };


  /* =================================================
     RENDER
  ================================================= */

  return (

    <main className="monitor-page monitor-match-only">

      {showPushPrompt && (
        <div className="monitor-push-prompt" role="dialog" aria-modal="true" aria-labelledby="monitor-push-prompt-title">
          <div className="monitor-push-prompt-icon">🔔</div>
          <strong id="monitor-push-prompt-title">เปิดแจ้งเตือน Match แบบเรียลไทม์</strong>
          <p>รับแจ้งเตือนเมื่อถึงคิวและเมื่อถึงเวลาลงสนาม แม้ไม่ได้มองหน้าจออยู่</p>
          <div>
            <button
              type="button"
              className="monitor-push-prompt-later"
              onClick={() => {
                sessionStorage.setItem('monitor_push_prompt_dismissed', '1');
                setShowPushPrompt(false);
              }}
            >
              ไว้ภายหลัง
            </button>
            <button
              type="button"
              className="monitor-push-prompt-enable"
              disabled={pushBusy}
              onClick={togglePushNotifications}
            >
              {pushBusy ? 'กำลังเปิด…' : '🔔 เปิดแจ้งเตือน'}
            </button>
          </div>
          {pushMessage && <small>{pushMessage}</small>}
        </div>
      )}

      {inAppAlert && (
        <div className={`monitor-inapp-alert monitor-inapp-${inAppAlert.type.toLowerCase()}`} role="alert" aria-live="assertive">
          <button type="button" className="monitor-inapp-close" onClick={() => setInAppAlert(null)} aria-label="ปิดแจ้งเตือน">×</button>
          <div className="monitor-inapp-bell">{inAppAlert.type === 'PLAY' ? '📣' : '🔔'}</div>
          <strong>{inAppAlert.title}</strong>
          <span>{inAppAlert.body}</span>
          <button type="button" className="monitor-inapp-confirm" onClick={() => {
            if (inAppAlert.acknowledgementKey) localStorage.setItem(inAppAlert.acknowledgementKey, '1');
            setInAppAlert(null);
          }}>รับทราบ</button>
        </div>
      )}


      {/* =================================================
         HERO
      ================================================= */}

      <header className="monitor-hero">


        <div className="monitor-brand">

          <span
            className="monitor-live-dot"
            aria-hidden="true"
          />


          <div>

            <span className="monitor-eyebrow">

              LIVE MATCH MONITOR

            </span>


            <h1>

              🏸{' '}

              {
                selectedGroup?.Group_Name ||
                'ดูคิวและสนาม'
              }

            </h1>


            <p>

              แสดงเกมที่กำลังเล่นและคิวรอลงสนามแบบทันที

            </p>

            <span className="monitor-online-viewers">👁 Online {onlineViewerCount} คน</span>

          </div>

        </div>


        {!loggedInUser?.user_id && (
          <section className="monitor-notification-login">
            <span>🔔</span>
            <div>
              <strong>การแจ้งเตือนแมท</strong>
              <small>เข้าสู่ระบบเพื่อรับแจ้งเตือนเมื่อถึงคิวและลงสนาม</small>
            </div>
            <button type="button" onClick={openNotificationLogin}>เข้าสู่ระบบ</button>
          </section>
        )}

        {loggedInUser?.user_id && (
          <div className="monitor-member-actions">
            <button type="button" className="monitor-profile-button" onClick={openMyProfile}>
              🔍 {loggedInUser.name || `รหัสผู้เล่น ${loggedInUser.user_id}`}
            </button>
            <button type="button" className="monitor-rank-button monitor-member-ranking" onClick={openRankTable}>
              🏆 Ranking
            </button>
            <button
              type="button"
              className="monitor-refresh-button monitor-member-refresh"
              aria-label={refreshing ? 'กำลังอัปเดตข้อมูล' : 'อัปเดตข้อมูล'}
              title={refreshing ? 'กำลังอัปเดตข้อมูล' : 'อัปเดตข้อมูล'}
              disabled={!selectedGroupId || refreshing}
              onClick={() => loadMatches({ silent: true })}
            >
              <span aria-hidden="true">{refreshing ? '⏳' : '↻'}</span>
            </button>
            <button
              type="button"
              className="monitor-help-button monitor-member-help"
              onClick={() => setShowMonitorHelp(true)}
              aria-haspopup="dialog"
            >
              <span aria-hidden="true">?</span> วิธีใช้งาน
            </button>
            <button type="button" className="monitor-checkout-button" disabled={checkingOut} onClick={logoutMyself}>
              {checkingOut ? '…' : '🚪 ออกจากระบบ'}
            </button>
            <button
              type="button"
              className={`monitor-push-button ${pushEnabled ? 'is-enabled' : ''}`}
              disabled={pushBusy}
              onClick={togglePushNotifications}
            >
              {pushBusy ? '…' : pushEnabled ? '🔔 แจ้งเตือน: เปิด' : '🔕 เปิดแจ้งเตือน'}
            </button>
          </div>
        )}

        {checkoutMessage && <small className="monitor-checkout-message">{checkoutMessage}</small>}
        {pushMessage && <small className="monitor-push-message">{pushMessage}</small>}


        {/* ================= TOOLBAR ================= */}

        {!loggedInUser?.user_id && <div className="monitor-toolbar">

          <button
            type="button"
            className="monitor-help-button"
            onClick={() => setShowMonitorHelp(true)}
            aria-haspopup="dialog"
          >
            <span aria-hidden="true">?</span> วิธีใช้งาน
          </button>

          <button
            type="button"

            className="monitor-refresh-button"

            aria-label={refreshing ? 'กำลังอัปเดตข้อมูล' : 'อัปเดตข้อมูล'}

            title={refreshing ? 'กำลังอัปเดตข้อมูล' : 'อัปเดตข้อมูล'}

            disabled={
              !selectedGroupId ||
              refreshing
            }

            onClick={() =>
              loadMatches({
                silent: true
              })
            }
          >

            <span aria-hidden="true">{refreshing ? '⏳' : '↻'}</span>

          </button>

        </div>}

        <nav className="monitor-section-shortcuts monitor-header-shortcuts" aria-label="ทางลัดส่วนการแข่งขัน">
          <button type="button" className="is-playing" onClick={() => document.getElementById('monitor-playing')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            <span aria-hidden="true">🔎</span><b>เกมที่กำลังเล่น</b>
          </button>
          <button type="button" className="is-queue" onClick={() => document.getElementById('monitor-queue')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            <span aria-hidden="true">🔎</span><b>คิวลงสนาม</b>
          </button>
          <button type="button" className="is-ended" onClick={() => document.getElementById('monitor-ended')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            <span aria-hidden="true">🔎</span><b>อัปเดตผลการแข่งขัน</b>
          </button>
        </nav>

      </header>

      {showMonitorHelp && (
        <div
          className="monitor-help-overlay"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setShowMonitorHelp(false);
          }}
        >
          <section
            className="monitor-help-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="monitor-help-title"
          >
            <header className="monitor-help-header">
              <div>
                <span>คู่มือสำหรับสมาชิกใหม่</span>
                <h2 id="monitor-help-title">วิธีใช้ Live Match Monitor</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowMonitorHelp(false)}
                aria-label="ปิดวิธีใช้งาน"
              >
                ×
              </button>
            </header>

            <div className="monitor-help-grid">
              <article><span>🏸</span><div><strong>เลือกก๊วน</strong><p>เลือกก๊วนที่ต้องการดูข้อมูลสด</p></div></article>
              <article><span>↻</span><div><strong>อัปเดตข้อมูล</strong><p>ดึงข้อมูลการแข่งขันล่าสุดทันที</p></div></article>
              <article><span>🏆</span><div><strong>อันดับคะแนน</strong><p>ดู Rank และคะแนนของสมาชิก</p></div></article>
              <article><span>🔔</span><div><strong>แจ้งเตือน</strong><p>เปิดเพื่อรับแจ้งเตือนเมื่อถึงคิว</p></div></article>
              <article><span>🔎</span><div><strong>ทางลัดการแข่งขัน</strong><p>ไปยังเกมที่เล่น คิว หรืออัปเดตผลการแข่งขัน</p></div></article>
              <article><span>👤</span><div><strong>รูปหรือชื่อผู้เล่น</strong><p>แตะเพื่อดูประวัติการเล่น</p></div></article>
              <article><span>⏱️</span><div><strong>เวลาบนสนาม</strong><p>แสดงเวลาตั้งแต่เริ่ม Match</p></div></article>
              <article><span>🟢</span><div><strong>ข้อมูลเรียลไทม์</strong><p>รายการจะอัปเดตเมื่อสถานะเปลี่ยน</p></div></article>
            </div>

            <button
              type="button"
              className="monitor-help-done"
              onClick={() => setShowMonitorHelp(false)}
            >
              เข้าใจแล้ว
            </button>
          </section>
        </div>
      )}

      {(
        <aside
          className={`monitor-guide-bot${showGuideBot ? '' : ' is-collapsed'}`}
          style={guideBotPosition ? { ...guideBotPosition, right: 'auto', bottom: 'auto' } : undefined}
          aria-label="Bot แนะนำการใช้งาน"
        >
          {showGuideBot ? (
            <>
          <header
            className="monitor-guide-bot-header"
            onPointerDown={startGuideBotDrag}
            onPointerMove={moveGuideBot}
            onPointerUp={stopGuideBotDrag}
            onPointerCancel={stopGuideBotDrag}
          >
            {guideImageFailed
              ? <span className="monitor-guide-avatar-fallback" aria-hidden="true">🐱</span>
              : <img className="monitor-guide-avatar" src={registerAssistantCat} alt="" aria-hidden="true" onError={() => setGuideImageFailed(true)} />}
            <div><strong>ผู้ช่วยหน้า Monitor</strong><small>ลากตรงนี้เพื่อย้ายตำแหน่ง</small></div>
            <button
              type="button"
              onClick={collapseGuideBot}
              aria-label="ย่อ Bot แนะนำ"
            >
              −
            </button>
          </header>

          <div className="monitor-guide-bot-content">
            <span className="monitor-guide-step-icon" aria-hidden="true">{MONITOR_GUIDE_STEPS[guideStep].icon}</span>
            <div>
              <small>ขั้นตอน {guideStep + 1}/{MONITOR_GUIDE_STEPS.length}</small>
              <strong>{MONITOR_GUIDE_STEPS[guideStep].title}</strong>
              <p>{MONITOR_GUIDE_STEPS[guideStep].text}</p>
            </div>
          </div>

          <button
            type="button"
            className="monitor-guide-action"
            onClick={() => openGuideTarget(MONITOR_GUIDE_STEPS[guideStep].targetId)}
          >
            {MONITOR_GUIDE_STEPS[guideStep].action}
          </button>

          <footer className="monitor-guide-controls">
            <button type="button" disabled={guideStep === 0} onClick={() => setGuideStep(step => step - 1)}>ก่อนหน้า</button>
            <span>
              {MONITOR_GUIDE_STEPS.map((step, index) => (
                <i className={index === guideStep ? 'active' : ''} key={step.title} />
              ))}
            </span>
            <button
              type="button"
              onClick={() => guideStep === MONITOR_GUIDE_STEPS.length - 1
                ? collapseGuideBot()
                : setGuideStep(step => step + 1)}
            >
              {guideStep === MONITOR_GUIDE_STEPS.length - 1 ? 'เสร็จแล้ว' : 'ถัดไป'}
            </button>
          </footer>
            </>
          ) : (
            <button
              type="button"
              className="monitor-guide-bot-open"
              onPointerDown={event => startGuideBotDrag(event, true)}
              onPointerMove={moveGuideBot}
              onPointerUp={stopGuideBotDrag}
              onPointerCancel={stopGuideBotDrag}
              onClick={() => {
                if (guideBotMovedRef.current) { guideBotMovedRef.current = false; return; }
                expandGuideBot();
              }}
              aria-label="เปิด Bot แนะนำ"
              title="เปิด Bot แนะนำ"
            >
              {guideImageFailed
                ? <span className="monitor-guide-open-fallback" aria-hidden="true">🐱</span>
                : <img src={registerAssistantCat} alt="" aria-hidden="true" draggable="false" onError={() => setGuideImageFailed(true)} />}
            </button>
          )}
        </aside>
      )}


      {/* =================================================
         ERROR
      ================================================= */}

      {error && (

        <div
          className="monitor-error"
          role="alert"
        >

          ❌ {error}

        </div>

      )}


      {/* =================================================
         CONTENT
      ================================================= */}

      {loading ? (

        <div className="monitor-loading">

          กำลังโหลดข้อมูลคิวและสนาม...

        </div>

      ) : groups.length === 0 ? (

        <section className="monitor-no-open-group" role="status">
          <div aria-hidden="true">🏸</div>
          <h2>ยังไม่มีก๊วนเปิดอยู่</h2>
          <p>ก๊วนน้ามาดเริ่ม 19.00–00.00</p>
          <small>จัดทุกวันอังคารและวันพฤหัสบดี • เปิด 10 สนาม</small>
          <strong>แล้วพบกันนะครับ</strong>
        </section>

      ) : (

        <MatchBoard
          matches={
            matches
          }
          courts={courts}

          onPlayerClick={
            openPlayerHistory
          }
          submittingMatchId={submittingMatchId}
          isAdmin={String(loggedInUser?.role || '').toLowerCase() === 'admin'}
          onEditResult={(match, winnerTeam) => submitMatchResult(match, winnerTeam, true)}
        />

      )}

      {loggedInUser?.user_id && localStorage.getItem('token') && (
        <div className={`monitor-wait-bell-wrap ${waitAlertOpen ? 'is-open' : ''}`} style={waitBellPosition ? { left: waitBellPosition.x, top: waitBellPosition.y, right: 'auto', bottom: 'auto' } : undefined}>
          {waitAlertFeedback && <p className="monitor-wait-feedback" role="status">{waitAlertFeedback}</p>}
          {waitAlertOpen && <div className="monitor-wait-message-list" aria-label="เลือกข้อความแจ้งผู้จัด">
            {['อย่าลืมชั้นนน', 'ใกล้ถึงคิวชั้นยัง', 'หลับรอแล้วนะะ', 'น้ามาดสุดหล่อออออ'].map((message, index) => (
              <button type="button" key={message} style={{ '--arc-index': index }} disabled={waitAlertBusy} onClick={() => sendWaitAlert(message)}><i>{index + 1}</i><span>{message}</span></button>
            ))}
          </div>}
          <button type="button" className={`monitor-floating-wait-bell ${waitAlertBusy ? 'is-busy' : ''}`} disabled={waitAlertBusy} aria-label="รอนาน กดเพื่อเลือกข้อความ หรือลากเพื่อย้ายตำแหน่ง"
            onPointerDown={event => { const rect = event.currentTarget.getBoundingClientRect(); waitBellMovedRef.current = false; waitBellDragRef.current = { pointerX: event.clientX, pointerY: event.clientY, left: rect.left, top: rect.top, width: rect.width, height: rect.height }; event.currentTarget.setPointerCapture?.(event.pointerId); }}
            onPointerMove={moveWaitBell} onPointerUp={finishWaitBellDrag} onPointerCancel={finishWaitBellDrag}
            onClick={() => { if (waitBellMovedRef.current) { waitBellMovedRef.current = false; return; } setWaitAlertFeedback(''); setWaitAlertOpen(open => !open); }}>
            <span aria-hidden="true">🔔</span><small>{waitAlertBusy ? 'กำลังแจ้ง…' : waitAlertOpen ? 'เลือกข้อความ' : 'รอนาน'}</small>
          </button>
        </div>
      )}

      {showScrollTop && (
        <button
          type="button"
          className="monitor-scroll-top"
          aria-label="เลื่อนกลับด้านบน"
          title="กลับด้านบน"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          ↑
        </button>
      )}

      {qrScanMessage && !qrScannerOpen && (
        <button type="button" className={`monitor-scan-feedback${qrScanError ? ' is-error' : ''}`} onClick={() => setQrScanMessage('')}>
          {qrScanError ? '⚠️' : '✅'} {qrScanMessage}<small>แตะเพื่อปิด</small>
        </button>
      )}

      {qrScannerOpen && (
        <div className="monitor-qr-backdrop" role="dialog" aria-modal="true" aria-label="สแกน QR Check-in">
          <section className="monitor-qr-modal">
            <header><strong>📷 สแกน QR Check-in</strong><button type="button" onClick={() => setQrScannerOpen(false)} aria-label="ปิดตัวสแกน">×</button></header>
            <div className="monitor-qr-camera"><video ref={qrVideoRef} muted playsInline /><span aria-hidden="true" /></div>
            <p>{qrScanMessage || 'วาง QR Code ให้อยู่ภายในกรอบ ระบบจะอ่านให้อัตโนมัติ'}</p>
          </section>
        </div>
      )}

      <BottomNavigation
        active="monitor"
        onMonitor={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        onMatches={() => navigate('/match-request')}
        onScan={openMonitorQrScanner}
        onReward={() => navigate('/rewards')}
        onProfile={() => navigate('/profile')}
      />

    </main>

  );

}


export default Monitor;
